/**
 * Seznam alternativních tras po řádcích + nemožné varianty s důvody.
 */
import { dayOffsetSuffix, formatDuration, formatTime } from '../core/time';
import type { Itinerary, InfeasibleResult } from '../core/types';
import { el, clear, fmtCzk, fmtKm, fmtCo2, fmtComfort } from './dom';
import { icon, iconOrEmoji, type IconName } from './icons';

export function renderResults(
  container: HTMLElement,
  itineraries: Itinerary[],
  infeasible: InfeasibleResult[],
  selectedId: string | null,
  onSelect: (id: string) => void,
): void {
  clear(container);

  if (itineraries.length === 0 && infeasible.length === 0) return;

  if (itineraries.length > 0) {
    const list = el('div', { className: 'results-list', role: 'list', 'aria-label': 'Nalezené trasy' });
    for (const it of itineraries) {
      list.append(el('div', { role: 'listitem' }, renderRow(it, it.id === selectedId, onSelect)));
    }
    container.append(list);
  }

  if (infeasible.length > 0) {
    const box = el('div', { className: 'infeasible-list' });
    for (const inf of infeasible) {
      box.append(
        el(
          'div',
          { className: 'infeasible-row' },
          el('span', { 'aria-hidden': 'true' }, iconOrEmoji(inf.icon, { size: 14 })),
          el('span', {}, el('strong', {}, inf.name + ': '), inf.reason),
        ),
      );
    }
    container.append(box);
  }
}

function renderRow(it: Itinerary, selected: boolean, onSelect: (id: string) => void): HTMLElement {
  const totalSegMin = Math.max(0.01, it.totalMin);

  const bar = el('div', { className: 'result-row__bar', 'aria-hidden': 'true' });
  for (const seg of it.segments) {
    const frac = Math.max(0.015, (seg.endMin - seg.startMin) / totalSegMin);
    bar.append(
      el('span', {
        className: 'result-row__bar-seg',
        style: `width:${(frac * 100).toFixed(2)}%;background:${seg.kind === 'wait' ? 'repeating-linear-gradient(45deg,#caa15a 0 4px,#e8d4ae 4px 8px)' : seg.color}`,
        title: seg.label,
      }),
    );
  }

  const meta = el(
    'div',
    { className: 'result-row__meta' },
    metaItem('clock', `${formatTime(it.departureMs)} → ${formatTime(it.arrivalMs)}${dayOffsetSuffix(it.departureMs, it.arrivalMs)}`, 'Odjezd → příjezd'),
    metaItem('ruler', fmtKm(it.distanceKm), 'Délka trasy'),
    it.waitMin > 0.4 ? metaItem('wait', `čekání ${formatDuration(it.waitMin)}`) : null,
    it.transfers > 0 ? metaItem('transfers', `${it.transfers}× přestup`) : null,
    metaItem('money', fmtCzk(it.cost), 'Cena / odhad nákladů'),
    metaItem('leaf', fmtCo2(it.emissions), 'Emise CO₂'),
    metaItem('comfort', fmtComfort(it.comfort), 'Komfort'),
    metaItem(
      'reliability',
      `±${Math.round(it.uncertaintyMin)} min (${Math.round(it.reliability * 100)} %)`,
      `Nejistota odhadu ±${Math.round(it.uncertaintyMin)} min při spolehlivosti ${Math.round(it.reliability * 100)} %`,
    ),
  );

  const badges = el('div', { className: 'result-row__badges' });
  if (it.windowNote) {
    badges.append(el('span', { className: 'badge badge--est', title: it.windowNote }, 'mimo okno'));
  }
  for (const b of it.badges) {
    const isRec = b === 'Doporučujeme';
    badges.append(
      el(
        'span',
        {
          className: `badge${isRec ? ' badge--rec' : ''}`,
          ...(isRec && it.recommendReason ? { title: it.recommendReason } : {}),
        },
        b,
      ),
    );
  }
  badges.append(
    el(
      'span',
      { className: `badge ${it.dataSource === 'estimate' ? 'badge--est' : 'badge--real'}` },
      it.dataSource === 'estimate' ? 'Odhad' : it.dataSource === 'mixed' ? 'Reálná trasa + odhad času' : 'Reálná data',
    ),
  );

  return el(
    'button',
    {
      className: 'result-row',
      type: 'button',
      'aria-pressed': String(selected),
      style: `--row-color:${it.color}`,
      onclick: () => onSelect(it.id),
    },
    el(
      'div',
      { className: 'result-row__top' },
      el('span', { className: 'result-row__icon', 'aria-hidden': 'true' }, iconOrEmoji(it.icon, { size: 20 })),
      el('span', { className: 'result-row__name' }, it.name),
      el('span', { className: 'result-row__time' }, formatDuration(it.totalMin)),
    ),
    bar,
    meta,
    badges,
  );
}

function metaItem(name: IconName, text: string, title?: string): HTMLElement {
  return el(
    'span',
    { className: 'meta-item', ...(title ? { title } : {}) },
    icon(name, { size: 13 }),
    el('strong', {}, text),
  );
}
